package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.ui.runtime.animation.Easing
import top.wcpe.allincore.ui.runtime.element.AutoScroll
import top.wcpe.allincore.ui.runtime.element.Box
import top.wcpe.allincore.ui.runtime.element.Checkbox
import top.wcpe.allincore.ui.runtime.element.CollapsingHeader
import top.wcpe.allincore.ui.runtime.element.Column
import top.wcpe.allincore.ui.runtime.element.Divider
import top.wcpe.allincore.ui.runtime.element.DragReorder
import top.wcpe.allincore.ui.runtime.element.Dropdown
import top.wcpe.allincore.ui.runtime.element.EntityPreview
import top.wcpe.allincore.ui.runtime.element.FontStyle
import top.wcpe.allincore.ui.runtime.element.InfiniteScroll
import top.wcpe.allincore.ui.runtime.element.ItemStack
import top.wcpe.allincore.ui.runtime.element.ModelPreview
import top.wcpe.allincore.ui.runtime.element.MultiColumnVirtualList
import top.wcpe.allincore.ui.runtime.element.PullToRefresh
import top.wcpe.allincore.ui.runtime.element.RadioButton
import top.wcpe.allincore.ui.runtime.element.RadioGroup
import top.wcpe.allincore.ui.runtime.element.Row
import top.wcpe.allincore.ui.runtime.element.ScrollView
import top.wcpe.allincore.ui.runtime.element.Slot
import top.wcpe.allincore.ui.runtime.element.Splitter
import top.wcpe.allincore.ui.runtime.element.Switch
import top.wcpe.allincore.ui.runtime.element.TabView
import top.wcpe.allincore.ui.runtime.element.Text
import top.wcpe.allincore.ui.runtime.element.Toast
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.element.VirtualGrid
import top.wcpe.allincore.ui.runtime.element.VirtualList
import top.wcpe.allincore.ui.runtime.element.VirtualMasonry
import top.wcpe.allincore.ui.runtime.element.VirtualTree
import top.wcpe.allincore.ui.runtime.element.Zoomable
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.render.layer.LayerRenderer
import top.wcpe.allincore.ui.runtime.render.layer.LayerRenderers
import top.wcpe.allincore.ui.runtime.render.layer.SlotBackgroundRenderer
import top.wcpe.allincore.ui.runtime.render.layer.SlotItemRenderer
import top.wcpe.allincore.ui.runtime.render.layer.SlotOverlayRenderer
import top.wcpe.allincore.ui.runtime.render.model.RenderLayer
import top.wcpe.allincore.ui.runtime.theme.ControlTheme
import top.wcpe.allincore.ui.runtime.theme.ThemeTransition
import kotlin.concurrent.Volatile
import kotlin.jvm.JvmStatic
import kotlin.reflect.KClass

/**
 * 渲染器注册表：按元素类型精确匹配 [Renderer]。
 *
 * 简单优先：仅按 `Class.equals` 精确匹配，不做继承链查找。
 *
 * 额外携带一份当前生效的 [ControlTheme]，由 `UIRenderer` 入口注入 `ThemeAccess`，
 * 供 8 个交互控件 Renderer 读取主题色。
 */
class RendererRegistry {

    private val renderers = HashMap<KClass<out UIElement<*>>, Renderer>()

    /**
     * 当前生效的控件主题。
     *
     * volatile：由 DSL action 主线程写（`local.setTheme`）、渲染主线程读（`UIRenderer.render`）。
     * 当前架构下双方天然同主线程，volatile 是面向未来跨线程访问的防御性约束。
     * 仅赋值 / 读取，无 read-modify-write 模式，原子性天然满足。
     */
    @Volatile
    private var theme: ControlTheme = ControlTheme.defaultFallback()

    /** 进行中的主题切换过渡；null 表示无过渡（getter 零开销直返 [theme]）。 */
    @Volatile
    private var transition: ThemeTransition? = null

    /** 全局默认字体。 */
    @Volatile
    private var globalFontValue: FontStyle? = null

    /** 页面级字体。 */
    @Volatile
    private var pageFontValue: FontStyle? = null

    /**
     * 注册一个类型对应的渲染器，支持流式调用。
     *
     * 类型上界放宽到 `Class<out UIElement<*>>`：多数控件自类型与自身一致
     * （`Foo extends UIElement<Foo>`），但继承型容器（如 `CarouselView extends PageView`）
     * 的自类型停留在父类，用 `UIElement<T>` 的强约束无法表达，故此处仅约束到 UIElement。
     */
    fun register(type: KClass<out UIElement<*>>, renderer: Renderer): RendererRegistry {
        renderers[type] = renderer
        return this
    }

    /** 按 `element::class` 精确查找；找不到返回 `null`。 */
    fun lookup(element: UIElement<*>): Renderer? = renderers[element::class]

    /** 设置控件主题，链式调用。 */
    fun controlTheme(theme: ControlTheme?): RendererRegistry {
        this.theme = theme ?: ControlTheme.defaultFallback()
        // 显式直接设置主题时取消进行中的过渡（瞬切语义）。
        transition = null
        return this
    }

    /**
     * 当前控件主题（永不返回 null）。
     *
     * 无过渡时零开销直返存量；存在主题切换过渡时按当前时间逐帧采样合成，过渡结束即落定为目标主题。
     * 调用发生在客户端渲染线程，单线程读改写安全。
     */
    fun controlTheme(): ControlTheme {
        val tr = transition ?: return theme
        val now = currentTimeMillis()
        if (tr.isDone(now)) {
            theme = tr.target()
            transition = null
            return theme
        }
        return tr.sample(now)
    }

    /**
     * 启动一次主题切换过渡：从「当前（可能正在插值的）主题」平滑过渡到 [target]。
     * [durationMs] <= 0 或起止相同则瞬切（与历史 [controlTheme] 行为一致）。
     */
    fun beginThemeTransition(target: ControlTheme?, durationMs: Long, easing: Easing): RendererRegistry {
        val dest = target ?: ControlTheme.defaultFallback()
        // 取当前插值主题，支持过渡中重定向不跳变。
        val current = controlTheme()
        if (durationMs <= 0 || current === dest) {
            theme = dest
            transition = null
            return this
        }
        transition = ThemeTransition(current, dest, currentTimeMillis(), durationMs, easing)
        return this
    }

    /** 设置全局默认字体，链式调用。 */
    fun globalFont(font: FontStyle?): RendererRegistry {
        globalFontValue = font
        return this
    }

    /** 当前全局字体（可能为 null）。 */
    fun globalFont(): FontStyle? = globalFontValue

    /** 设置页面级字体，链式调用。 */
    fun pageFont(font: FontStyle?): RendererRegistry {
        pageFontValue = font
        return this
    }

    /** 当前页面级字体（可能为 null）。 */
    fun pageFont(): FontStyle? = pageFontValue

    companion object {
        /**
         * 默认注册表：注册全部内置控件渲染器。
         *
         * 留守渲染器（Button/Image/ProgressBar/TextField/Canvas/PageView/Slider/Modal，
         * 与 element 留守清单联动）经 [registerLeaveBehindRenderers] 在 JVM 侧补挂（ADR-0072 决策 4）；
         * JS 端无留守元素，补挂为 no-op。
         */
        @JvmStatic
        fun defaultRegistry(): RendererRegistry = registerLeaveBehindRenderers(commonRegistry())

        /** 全部 commonMain 内置控件渲染器的注册（两端共享段）。 */
        private fun commonRegistry(): RendererRegistry = RendererRegistry()
            .register(Box::class, BoxRenderer())
            .register(Row::class, BoxRenderer())
            .register(Column::class, BoxRenderer())
            // Task 2c-E1：splitter 分隔容器按容器渲染（两栏 + 分隔条由作者层布局）
            .register(Splitter::class, BoxRenderer())
            .register(Text::class, TextRenderer())
            .register(Divider::class, DividerRenderer())
            .register(Checkbox::class, CheckboxRenderer())
            .register(
                ItemStack::class,
                LayerRenderers(
                    LayerRenderer(RenderLayer.BACKGROUND, ItemStackBackgroundRenderer()),
                    LayerRenderer(RenderLayer.ITEM, ItemStackItemRenderer()),
                    LayerRenderer(RenderLayer.OVERLAY, ItemStackOverlayRenderer()),
                ),
            )
            .register(
                Slot::class,
                LayerRenderers(
                    LayerRenderer(RenderLayer.BACKGROUND, SlotBackgroundRenderer()),
                    LayerRenderer(RenderLayer.ITEM, SlotItemRenderer()),
                    LayerRenderer(RenderLayer.OVERLAY, SlotOverlayRenderer()),
                ),
            )
            .register(EntityPreview::class, EntityPreviewRenderer())
            .register(ModelPreview::class, ModelPreviewRenderer())
            .register(Switch::class, SwitchRenderer())
            .register(RadioButton::class, RadioButtonRenderer())
            .register(RadioGroup::class, RadioGroupRenderer())
            .register(Dropdown::class, DropdownRenderer())
            .register(ScrollView::class, ScrollableContainerRenderer())
            .register(VirtualList::class, ScrollableContainerRenderer())
            .register(VirtualGrid::class, ScrollableContainerRenderer())
            .register(VirtualMasonry::class, ScrollableContainerRenderer())
            .register(VirtualTree::class, ScrollableContainerRenderer())
            .register(MultiColumnVirtualList::class, MultiColumnVirtualListRenderer())
            .register(PullToRefresh::class, BoxRenderer())
            .register(InfiniteScroll::class, BoxRenderer())
            .register(CollapsingHeader::class, BoxRenderer())
            .register(Zoomable::class, BoxRenderer())
            .register(AutoScroll::class, BoxRenderer())
            .register(DragReorder::class, BoxRenderer())
            .register(TabView::class, TabViewRenderer())
            .register(Toast::class, BoxRenderer())
    }
}

/**
 * 留守渲染器补挂接缝（ADR-0072 决策 4）：JVM actual 注册 Button/Image/ProgressBar/TextField/
 * Canvas/PageView(CarouselView)/Slider/Modal 的留守渲染器；JS actual 原样返回（无留守元素）。
 * 留守元素迁 commonMain 后本接缝随之收敛删除。
 */
internal expect fun registerLeaveBehindRenderers(registry: RendererRegistry): RendererRegistry
